import React from "react";
import { Dialog } from "@headlessui/react";
import { Mail, ShieldCheck, UserCircle2, X } from "lucide-react";
import { useTheme } from "./ThemeProvider";

export default function ProfileModal({ open, onClose, user, displayName, roleLabel }) {
    const { currentTheme } = useTheme();

    return (
        <Dialog open={open} onClose={onClose} className="fixed inset-0 z-[100]">
            <div className="fixed inset-0 bg-slate-950/35 backdrop-blur-sm" aria-hidden="true" />

            <div className="fixed inset-0 flex items-center justify-center p-4">
                <Dialog.Panel className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.18)]">
                    <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.06)_0%,rgba(148,163,184,0.04)_55%,rgba(255,255,255,0)_100%)]" />

                    <button
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-slate-500 shadow-sm transition hover:text-slate-900"
                        aria-label="Close profile"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative p-6 sm:p-7">
                        <Dialog.Title className="text-sm font-semibold uppercase tracking-[0.26em] text-slate-400">
                            Profile
                        </Dialog.Title>

                        <div className="mt-5 flex flex-col items-center text-center">
                            <div className="relative">
                                <div className="absolute inset-0 rounded-full bg-slate-200 blur-xl opacity-60" />
                                <div className="relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-slate-100 shadow-lg">
                                    {user?.profile_image ? (
                                        <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
                                    ) : (
                                        <UserCircle2 className="h-14 w-14 text-slate-400" />
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 space-y-1">
                                <div className="text-2xl font-semibold tracking-tight text-slate-900">
                                    {displayName}
                                </div>
                                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    {roleLabel}
                                </div>
                            </div>
                        </div>

                        <div className="mt-7 space-y-3">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm">
                                        <Mail className="h-4.5 w-4.5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
                                            Email
                                        </p>
                                        <p className="truncate text-sm font-medium text-slate-800">
                                            {user?.email || "No email available"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-slate-500 shadow-sm">
                                        <ShieldCheck className="h-4.5 w-4.5" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
                                            Access Level
                                        </p>
                                        <p className="text-sm font-medium text-slate-800">
                                            {roleLabel}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="mt-6 w-full rounded-2xl py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90"
                            style={{ background: currentTheme.color }}
                        >
                            Close
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
